/* eslint-disable no-restricted-syntax */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  memo,
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { Plus } from '@saleshandy/icons';
import { useDispatch } from 'react-redux';

import {
  AiSequenceGeneratorData,
  IdealCustomerProfile,
  SelectableCard,
} from '../../../type';
import StepWrapper from '../../../../../shared/stepwrapper';
import AiSequenceModalFooter from '../ai-sequence-modal-footer/ai-sequence-modal-footer';
import { AccordionCard } from '../../../../../shared/accordion-card/accordioncard';
import {
  resetHasStepSkipped,
  setAiSeqChatMessage,
  setHasAiSeqStepSkipped,
} from '../../../../../sequence-slice';
import { truncate } from '../../../../../../../shared/utils';

interface IcpForBusinessStepProps {
  idealCustomerProfiles: IdealCustomerProfile[];
  aiSeqUserData: AiSequenceGeneratorData;
  saveCurrentStepDetails?: (payload: any) => void;
}

/**
 *
 * @prop idealCustomerProfiles - It will display the ICP's get from ai.
 * @prop aiSeqUserData - it will show the data which is collected till current step.
 * @prop saveCurrentStepDetails - Function to store current step details.
 *
 * @returns React Component
 */
const IcpForBusinessStep: React.FC<IcpForBusinessStepProps> = ({
  idealCustomerProfiles = [],

  aiSeqUserData,
  saveCurrentStepDetails = () => {},
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [items, setItems] = useState<SelectableCard[]>([]);
  const dispatch = useDispatch();

  const itemRefs = useRef<
    Record<number, React.MutableRefObject<() => boolean>>
  >({});

  const handleAddItem = useCallback(() => {
    const newId = Date.now();
    itemRefs.current[newId] = { current: () => true };
    setItems((prev) => [
      ...prev,
      {
        id: newId,
        heading: '',
        description: '',
        isEditable: true,
        isNew: true,
        isError: false,
      },
    ]);
    setExpandedId(newId);
    setSelectedIds((prev) => [...prev, newId]);
  }, []);

  const handleUpdate = useCallback(
    (id: number, heading: string, description: string) => {
      setItems((prev) =>
        prev?.map((item) =>
          item?.id === id
            ? { ...item, heading, description, isEditable: false }
            : item,
        ),
      );
    },
    [],
  );

  const handleDelete = useCallback((id: number) => {
    setItems((prev) => prev?.filter((item) => item?.id !== id));
    delete itemRefs.current[id];
    setExpandedId((prev) => (prev === id ? null : prev));
    setSelectedIds((prev) => prev?.filter((i) => i !== id));
  }, []);

  const handleSelect = useCallback((item: SelectableCard) => {
    setSelectedIds((prev) =>
      prev?.includes(item?.id)
        ? prev?.filter((id) => id !== item?.id)
        : [...prev, item?.id],
    );
  }, []);

  const handleExpand = useCallback((item: SelectableCard) => {
    setExpandedId((prev) => (prev === item.id ? null : item.id));
  }, []);

  const isDisabled = useMemo(
    () => items?.filter((item) => item?.isNew)?.length >= 3,
    [items],
  );

  const selectedItemsHaveError = useMemo(
    () =>
      selectedIds
        ?.map((id) => items?.find((item) => item?.id === id)?.isError)
        ?.some(Boolean),
    [selectedIds, items],
  );

  const handleSaveIcpForBusinessStepDetails = useCallback(() => {
    const payload = items
      ?.filter((item) => selectedIds?.includes(item?.id))
      ?.map((item) => ({
        name: item?.heading?.trim(),
        description: item?.description?.trim(),
        ...(item?.isNew && {
          isCustom: true,
        }),
      }));
    const icpHeadings = payload?.map((item) => item?.name)?.join(', ');
    dispatch(setAiSeqChatMessage(icpHeadings));
    saveCurrentStepDetails({ idealCustomerProfiles: payload });
  }, [items, selectedIds, saveCurrentStepDetails]);

  const handleSkipIcpForBusinessStepDetails = useCallback(() => {
    dispatch(setHasAiSeqStepSkipped(true));
    saveCurrentStepDetails({ hasSkipped: true });
    dispatch(resetHasStepSkipped());
  }, [saveCurrentStepDetails]);

  useEffect(() => {
    setItems(
      idealCustomerProfiles?.map((profile, index) => ({
        id: index + 1,
        heading: profile?.name,
        description: profile?.description,
        isEditable: false,
        isNew: false,
        isError: false,
      })),
    );
  }, [idealCustomerProfiles]);

  return (
    <StepWrapper
      heading={`Ideal Customer Profiles (ICP) for ${truncate(
        aiSeqUserData?.companyInfo?.name,
        26,
        false,
      )}`}
    >
      {items?.map((item) => {
        if (!itemRefs.current[item?.id]) {
          itemRefs.current[item?.id] = { current: () => true };
        }

        return (
          <AccordionCard
            key={item?.id}
            type="checkbox"
            selected={selectedIds?.includes(item?.id)}
            onSelect={() => handleSelect(item)}
            heading={item?.heading}
            description={item?.description}
            isNew={item?.isNew}
            expanded={expandedId === item?.id}
            onExpand={() => {
              if (expandedId !== null && itemRefs.current[expandedId]) {
                itemRefs.current[expandedId].current?.();
              }
              handleExpand(item);
            }}
            onCollapse={() => {
              itemRefs.current[item.id]?.current?.();
              handleExpand(item);
            }}
            onUpdate={(heading, description) =>
              handleUpdate(item.id, heading, description)
            }
            onDelete={item?.isNew ? () => handleDelete(item?.id) : undefined}
            placeholderHeading="Name your ideal customer profile"
            placeholderDescription="Describe your ideal customer in a few words..."
            validateRef={itemRefs.current[item?.id]}
            descriptionLabel="ICP Description"
            headingLabel="ICP Name"
            dummyHeading="Name your ideal customer profile"
            dummyDescription="Describe your ideal customer in a few words..."
            onValidationResult={(hasError) => {
              setItems((prev) =>
                prev.map((el) =>
                  el?.id === item?.id ? { ...el, isError: hasError } : el,
                ),
              );
            }}
          />
        );
      })}
      <div
        className={`add-feature ${isDisabled ? 'disabled' : ''}`}
        onMouseDown={isDisabled ? undefined : handleAddItem}
      >
        <Plus width={16} height={16} />
        <span>Add ICPs</span>
      </div>

      <AiSequenceModalFooter
        showSkipBtn={true}
        disableContinueBtn={selectedItemsHaveError || selectedIds?.length === 0}
        onClickContinue={handleSaveIcpForBusinessStepDetails}
        onClickSkip={handleSkipIcpForBusinessStepDetails}
      />
    </StepWrapper>
  );
};

export default memo(IcpForBusinessStep);
